/**
 * StudioPreview.tsx — Pré-visualização isolada do modelo
 *
 * Usa o MESMO compilador HTML da geração de PDF (Studio Core / renderizador
 * HTML compartilhado com as Edge Functions). Não existe segundo motor aqui:
 * o que aparece na tela é exatamente o que o servidor vai gerar.
 *
 * A exibição acontece dentro de um `iframe` com `sandbox` vazio: nada do
 * conteúdo tem acesso ao painel, a cookies ou a armazenamento.
 *
 * RELEASE: v1.1.0 - 2026-09-11 - Documento estruturado passa pelo montador
 *          canônico de página, incluindo tema, largura, fundo e destaque.
 * RELEASE: v1.0.0 - 2026-09-09 - Versão inicial (Studio, Fase 4)
 */

import React, { useMemo } from 'react';
import type { StudioDocument } from '@/lib/studio/core';
import { buildStudioPageHtml } from '../../../supabase/functions/_shared/studio/pageDocument';

/** Larguras usadas na alternância computador/celular. */
export const STUDIO_PREVIEW_WIDTHS: Record<'desktop' | 'mobile', string> = {
  desktop: '100%',
  mobile: '390px',
};

interface StudioPreviewProps {
  /** Documento em blocos (modo estruturado). */
  document?: StudioDocument | null;
  /** HTML importado (modo código) — exibido como está, sem executar scripts. */
  html?: string | null;
  /** Aparelho simulado. */
  device?: 'desktop' | 'mobile';
  className?: string;
}

export function StudioPreview({
  document: documento,
  html,
  device = 'desktop',
  className,
}: StudioPreviewProps) {
  const conteudo = useMemo(() => {
    if (html) return html;
    return buildStudioPageHtml({
      document: documento ?? { schemaVersion: 1, blocks: [], theme: {}, settings: {} },
      sourceFormat: 'blocks',
      fallbackTitle: 'Pré-visualização',
      forceNoIndex: true,
    });
  }, [documento, html]);

  return (
    <div className={className}>
      <div
        className="mx-auto h-full overflow-hidden rounded-lg border bg-white shadow-sm transition-all"
        style={{ width: STUDIO_PREVIEW_WIDTHS[device], maxWidth: '100%' }}
      >
        <iframe
          title="Pré-visualização do modelo"
          // Isolamento total: sem scripts, sem acesso ao painel
          sandbox=""
          srcDoc={conteudo}
          className="h-full min-h-[480px] w-full border-0"
        />
      </div>
    </div>
  );
}
